import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Eye, Lock, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "../lib/api";
import { Button, Card, Dl, ErrorBox, Field, Input, Spinner, Textarea } from "../components/kd";
import { fmtDate, fmtDateTime } from "../lib/format";
import { DateInputDE } from "../components/DateInputs";
import { KI_HINT, MicButton, useKi } from "../components/Ki";
import { SnippetTextarea } from "../components/Snippets";

function ReportDraft({ fid, onApply }: { fid: string; onApply: (f: Record<string, string>) => void }) {
  const ctx = { fall_id: fid };
  const flags = useKi(ctx);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  if (!flags.assistent && !flags.sprache) return null;
  const decide = (entscheidung: string) => api.post("/ki/entscheidung", { ...ctx, entscheidung, aktion: "bericht" }).catch(() => undefined);
  const make = async () => {
    setBusy(true);
    try { const r = await api.post("/ki/text", { ...ctx, aktion: "bericht", text: notes }, { timeout: 90000 }); setDraft(r.data.felder); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mb-5 rounded-lg border border-border bg-bg p-4" data-testid="report-draft">
      <h3 className="mb-2">Entwurf aus Stichpunkten</h3>
      <Textarea aria-label="Stichpunkte" rows={3} placeholder="z. B. 38 osteotomiert, Naht 4-0, komplikationslos, Nahtentfernung 7 Tage" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="report-draft-notes" />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {flags.sprache && <MicButton ctx={ctx} onText={(t) => setNotes((x) => (x ? `${x} ${t}` : t))} testid="report-draft-mic" />}
        {flags.assistent && <Button variant="secondary" loading={busy} disabled={!notes.trim()} onClick={make} data-testid="report-draft-button"><Sparkles className="h-4 w-4" aria-hidden />Entwurf erstellen</Button>}
      </div>
      {draft && (
        <div className="mt-3 rounded-lg border border-primary bg-surface p-3" data-testid="ki-preview">
          <Dl items={FIELDS.map(([k, l]) => [l, <span className={draft[k] === "[fehlt]" ? "text-[#7A3E00]" : ""} data-testid={`draft-${k}`}>{draft[k]}</span>])} />
          <p className="mt-1 text-xs text-text-muted">{KI_HINT}</p>
          <div className="mt-2 flex gap-2">
            <Button onClick={() => { decide("uebernommen"); onApply(Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, v === "[fehlt]" ? "" : v]))); setDraft(null); }} data-testid="ki-accept">In Felder übernehmen</Button>
            <Button variant="ghost" onClick={() => { decide("verworfen"); setDraft(null); }} data-testid="ki-discard">Verwerfen</Button>
          </div>
          <p className="mt-2 text-xs text-text-muted">Mit [fehlt] markierte Felder bleiben leer und müssen von Ihnen ergänzt werden.</p>
        </div>)}
    </div>
  );
}

function FieldMic({ fid, onText }: { fid: string; onText: (t: string) => void }) {
  const flags = useKi({ fall_id: fid });
  return flags.sprache ? <div className="mt-1"><MicButton ctx={{ fall_id: fid }} onText={onText} testid="report-field-mic" /></div> : null;
}

const FIELDS: [string, string, boolean][] = [
  ["diagnose", "Diagnose", true], ["therapie", "Durchgeführte Therapie", true], ["empfehlung", "Empfehlung zur Weiterbehandlung", true],
  ["wiedervorstellung", "Wiedervorstellung (optional)", false], ["bemerkung", "Bemerkung (optional)", false],
];
const EMPTY = { diagnose: "", therapie: "", behandlungsdatum: "", empfehlung: "", wiedervorstellung: "", bemerkung: "" };

export default function Report() {
  const { id } = useParams();
  const [d, setD] = useState<any>(null);
  const [form, setForm] = useState<any>(EMPTY);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [pdf, setPdf] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);

  const load = () => api.get(`/praxis/faelle/${id}/bericht`).then((r) => {
    setD(r.data);
    if (r.data.bericht) setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, r.data.bericht[k] || ""])) });
  }).catch((e) => setErr(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const body = () => ({ ...form, wiedervorstellung: form.wiedervorstellung || null, bemerkung: form.bemerkung || null });
  const check = () => {
    const missing = FIELDS.filter(([k, , r]) => r && !form[k].trim()).map(([, l]) => l);
    if (!form.behandlungsdatum) missing.push("Behandlungsdatum");
    if (missing.length) { setErr(`Bitte füllen Sie die Pflichtfelder aus: ${missing.join(", ")}.`); return false; }
    setErr(""); return true;
  };
  const run = async (kind: string, fn: () => Promise<any>) => {
    if (!check()) return;
    setBusy(kind);
    try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(null); }
  };
  const preview = () => run("vorschau", async () => {
    const r = await api.post(`/praxis/faelle/${id}/bericht/vorschau`, body(), { responseType: "blob" });
    if (pdf) URL.revokeObjectURL(pdf);
    setPdf(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })));
  });
  const send = () => run("versenden", async () => { await api.post(`/praxis/faelle/${id}/bericht/versenden`, body()); toast.success("Bericht versendet."); setConfirm(false); setPdf(null); load(); });

  if (!d) return err ? <ErrorBox>{err}</ErrorBox> : <Spinner />;
  const ro = d.schreibgeschuetzt;
  return (
    <div className="kd-fade mx-auto max-w-4xl">
      <Link to={`/praxis/faelle/${id}`} className="mb-3 inline-flex min-h-[44px] items-center gap-1" data-testid="back-to-case"><ArrowLeft className="h-4 w-4" aria-hidden />Zurück zum Fall</Link>
      <h1 className="mb-1">Bericht zu Fall {d.fall.fallnummer}</h1>
      <p className="mb-5 text-text-muted">Patient: {d.fall.patient}, geb. {fmtDate(d.fall.patient_geburtsdatum)}</p>
      {ro ? (
        <Card data-testid="report-readonly">
          <p className="mb-4 flex items-center gap-2 rounded-lg bg-success-bg p-3 text-success"><Lock className="h-5 w-5" aria-hidden />Versendet am {fmtDateTime(d.bericht.versendet_am)} – schreibgeschützt</p>
          <Dl items={[["Diagnose", d.bericht.diagnose], ["Therapie", d.bericht.therapie], ["Behandlungsdatum", fmtDate(d.bericht.behandlungsdatum)], ["Empfehlung", d.bericht.empfehlung], ["Wiedervorstellung", d.bericht.wiedervorstellung], ["Bemerkung", d.bericht.bemerkung]]} />
          {d.url && <a href={fileUrl(d.url)} className="mt-5 inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-primary px-4 font-medium text-white no-underline hover:bg-primary-dark" data-testid="report-download"><Download className="h-4 w-4" aria-hidden />PDF herunterladen</a>}
        </Card>
      ) : !d.kann_erstellen ? (
        <Card><p>Ein Bericht kann erst ab Status „Behandelt“ erstellt werden.</p></Card>
      ) : (
        <Card>
          <ErrorBox testid="report-error">{err}</ErrorBox>
          <ReportDraft fid={id as string} onApply={(v) => setForm({ ...form, ...Object.fromEntries(Object.entries(v).filter(([, x]) => x)) })} />
          {FIELDS.slice(0, 2).map(([k, l, r]) => (
            <Field key={k} label={l} required={r} htmlFor={k}><SnippetTextarea id={k} value={form[k] || ""} onValue={(v) => setForm((x: any) => ({ ...x, [k]: v }))} data-testid={`report-${k}-input`} />
              <FieldMic fid={id as string} onText={(t) => setForm((x: any) => ({ ...x, [k]: x[k] ? `${x[k]} ${t}` : t }))} /></Field>
          ))}
          <Field label="Behandlungsdatum" required htmlFor="bd"><DateInputDE id="bd" className="max-w-[220px]" value={form.behandlungsdatum} onChange={(v) => setForm({ ...form, behandlungsdatum: v })} data-testid="report-behandlungsdatum-input" /></Field>
          {FIELDS.slice(2).map(([k, l, r]) => (
            <Field key={k} label={l} required={r} htmlFor={k}>
              {k === "wiedervorstellung" ? <Input id={k} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`report-${k}-input`} />
                : <><SnippetTextarea id={k} value={form[k] || ""} onValue={(v) => setForm((x: any) => ({ ...x, [k]: v }))} data-testid={`report-${k}-input`} />
                  <FieldMic fid={id as string} onText={(t) => setForm((x: any) => ({ ...x, [k]: x[k] ? `${x[k]} ${t}` : t }))} /></>}
            </Field>
          ))}
          <div className="flex flex-wrap gap-3 border-t border-border pt-5">
            <Button variant="secondary" onClick={preview} loading={busy === "vorschau"} data-testid="report-preview-button"><Eye className="h-4 w-4" aria-hidden />Vorschau</Button>
            <Button onClick={() => check() && setConfirm(true)} disabled={d.fall.status !== "BEHANDELT"} data-testid="report-send-button"><Send className="h-4 w-4" aria-hidden />Bericht versenden</Button>
          </div>
          {confirm && (
            <div className="kd-fade mt-4 rounded-lg border border-primary bg-primary-light p-4" data-testid="report-confirm">
              <p className="mb-3">Nach dem Versand ist der Bericht schreibgeschützt und der Überweiser wird per E-Mail benachrichtigt. Jetzt versenden?</p>
              <div className="flex gap-2">
                <Button onClick={send} loading={busy === "versenden"} data-testid="report-confirm-send-button">Ja, versenden</Button>
                <Button variant="ghost" onClick={() => setConfirm(false)} data-testid="report-confirm-cancel">Abbrechen</Button>
              </div>
            </div>
          )}
          {pdf && <iframe title="PDF-Vorschau" src={pdf} className="mt-5 h-[80vh] w-full rounded-lg border border-border" data-testid="report-preview-frame" />}
        </Card>
      )}
    </div>
  );
}
